import { useState, useEffect } from "react";
import { api } from "@/api/client";
import { X, Box, Package, Check } from "lucide-react";
import { calculateBundles } from "@/utils";

export default function BoxPackingModal({ mailing, onClose, onSave }) {
  const [boxSizes, setBoxSizes] = useState([]);
  const [selectedBoxId, setSelectedBoxId] = useState(mailing.selected_box_id || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [packingResult, setPackingResult] = useState(null);

  useEffect(() => {
    loadBoxSizes();
  }, []);

  useEffect(() => {
    if (selectedBoxId) {
      calculatePacking(selectedBoxId);
    }
  }, [selectedBoxId]);

  const loadBoxSizes = async () => {
    try {
      const data = await api.entities.BoxSize.list();
      setBoxSizes(data);
      
      // If no box selected, auto-select the first one
      if (!selectedBoxId && data.length > 0) {
        setSelectedBoxId(data[0].id);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load box sizes");
    } finally {
      setLoading(false);
    }
  };

  const calculatePacking = (boxId) => {
    const selectedBox = boxSizes.find((b) => b.id === boxId);
    if (!selectedBox || !mailing.routes) {
      setPackingResult(null);
      return;
    }

    const bundlesPerBox = selectedBox.bundles || 1;
    const boxes = [];
    let currentBox = {
      box_number: 1,
      routes: [],
      bundles: 0,
      pieces: 0,
      post_offices: new Set(),
    };

    // Sort routes by post office to keep them together
    const sortedRoutes = [...mailing.routes].sort((a, b) => {
      const poA = a.post_office || "";
      const poB = b.post_office || "";
      return poA.localeCompare(poB);
    });

    sortedRoutes.forEach((route) => {
       const bundleCalc = calculateBundles(route.pieces || 0);
       let remainingBundles = route.bundles || bundleCalc.bundles;

       // Handle oversized routes that exceed box capacity
       while (remainingBundles > 0) {
         // If adding remaining bundles exceeds box capacity, start a new box
         if (currentBox.bundles + remainingBundles > bundlesPerBox && currentBox.routes.length > 0) {
           boxes.push({ ...currentBox, container_size: selectedBox, post_offices: Array.from(currentBox.post_offices) });
           currentBox = {
             box_number: boxes.length + 1,
             routes: [],
             bundles: 0,
             pieces: 0,
             post_offices: new Set(),
           };
         }

         // Calculate how many bundles fit in this box
         const bundlesToFit = Math.min(remainingBundles, bundlesPerBox - currentBox.bundles);

         currentBox.routes.push({
           route: route.route,
           zip: route.zip,
           bundles: bundlesToFit
         });
         currentBox.bundles += bundlesToFit;
         currentBox.pieces += Math.round((bundlesToFit / (remainingBundles / (route.pieces || 1))) || 0);
         currentBox.post_offices.add(route.post_office || "Default Post Office");

         remainingBundles -= bundlesToFit;
       }
     });

    // Add the last box
    if (currentBox.routes.length > 0) {
      boxes.push({ ...currentBox, container_size: selectedBox, post_offices: Array.from(currentBox.post_offices) });
    }

    // Normalize box_number -> container_number to match packing.js structure
    const normalizedBoxes = boxes.map(b => ({ ...b, container_number: b.box_number }));

    setPackingResult({
      box_size: selectedBox,
      container_type: selectedBox.type || 'Box',
      boxes: normalizedBoxes,
      total_boxes: normalizedBoxes.length,
      total_bundles: mailing.total_bundles,
    });
  };

  const handleSave = async () => {
    if (!packingResult) {
      setError("Please select a box size");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const updateData = {
        selected_box_id: selectedBoxId,
        box_packing: packingResult,
      };

      await api.entities.Mailing.update(mailing.id, updateData);
      onSave({ ...mailing, ...updateData });
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save packing configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}>
        <div className="card" style={{ maxWidth: "800px", width: "90%", maxHeight: "90vh", overflow: "auto" }}>
          <div className="loading">Loading box sizes...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "1rem",
    }}>
      <div className="card" style={{ maxWidth: "900px", width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <h3 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#1e293b" }}>
              Pack Into Boxes
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginTop: "0.25rem" }}>
              {mailing.mailing_name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              color: "#64748b",
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        {/* Box Size Selection */}
        <div className="form-section">
          <div className="form-title">Select Box Size</div>
          {boxSizes.length === 0 ? (
            <p style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>
              No box sizes defined. Please go to Settings to add box sizes.
            </p>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
            }}>
              {boxSizes.map((box) => (
                <div
                  key={box.id}
                  onClick={() => setSelectedBoxId(box.id)}
                  style={{
                    padding: "1rem",
                    border: `2px solid ${selectedBoxId === box.id ? "#3b82f6" : "#e2e8f0"}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: selectedBoxId === box.id ? "#eff6ff" : "white",
                    transition: "all 0.3s ease",
                    position: "relative",
                  }}
                >
                  {selectedBoxId === box.id && (
                    <div style={{
                      position: "absolute",
                      top: "0.5rem",
                      right: "0.5rem",
                      background: "#3b82f6",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}>
                      <Check size={16} />
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <Box size={20} style={{ color: "#3b82f6" }} />
                    <div style={{ fontWeight: 600, fontSize: "1rem" }}>{box.name}</div>
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                    {box.length}" × {box.width}" × {box.height}"
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "#3b82f6", fontWeight: 600, marginTop: "0.5rem" }}>
                    Holds {box.bundles} bundle{box.bundles !== 1 ? "s" : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Packing Result */}
        {packingResult && (
          <div className="form-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div className="form-title" style={{ marginBottom: 0 }}>
                Packing Summary
              </div>
              <div style={{
                background: "#3b82f6",
                color: "white",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                fontWeight: 600,
              }}>
                {packingResult.total_boxes} Box{packingResult.total_boxes !== 1 ? "es" : ""}
              </div>
            </div>

            {/* Boxes Grid */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {packingResult.boxes.map((box, index) => (
                <div
                  key={index}
                  className="card"
                  style={{
                    borderLeft: "4px solid #3b82f6",
                    padding: "1rem",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <Box size={20} style={{ color: "#3b82f6" }} />
                      <span style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b" }}>
                        Box #{box.box_number}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "1rem", fontSize: "0.875rem" }}>
                      <span>
                        <strong>{box.bundles}</strong> bundles
                      </span>
                      <span>
                        <strong>{box.pieces.toLocaleString()}</strong> pieces
                      </span>
                    </div>
                  </div>

                  {/* Routes */}
                  <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.5rem" }}>
                    <strong style={{ color: "#475569" }}>Routes:</strong> {box.routes.map(r => r.route || r).join(", ")}
                  </div>

                  {/* Post Offices */}
                  {box.post_office_name && (
                    <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                      <strong style={{ color: "#475569" }}>Destination:</strong>{" "}
                      {box.post_office_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.5rem" }}>
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={saving || !packingResult}
          >
            <Package size={20} />
            {saving ? "Saving..." : "Save Packing"}
          </button>
        </div>
      </div>
    </div>
  );
}